import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRACTICE_OPTIONS = [
  "Tax Laws",
  "Litigation",
  "Conveyance",
  "Contracts",
  "Company Formation & Investment",
  "Arbitration",
  "Intellectual Property",
  "General Consultancy",
  "Debt Collection",
  "Other",
];

const inputCls =
  "w-full bg-navy-950/70 border border-gold/20 rounded-xl px-4 py-3.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:border-gold/70 transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    practice_area: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received. The firm will respond shortly.");
      setForm({ name: "", email: "", phone: "", practice_area: "", message: "" });
    } catch {
      toast.error("Could not send your enquiry. Please call or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <FadeUp>
              <SectionLabel>Consult the Firm</SectionLabel>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
                Your Matter Deserves <span className="text-gold">an Answer</span>
              </h2>
              <p className="mt-6 text-ink-dim leading-relaxed max-w-md">
                Share the outline of your matter below. Please also send an
                advance copy of any formal letter via e-mail.
              </p>
            </FadeUp>

            <FadeUp delay={0.15}>
              <div className="mt-10 space-y-5">
                <a
                  href="mailto:sakimlaw@gmail.com"
                  data-testid="contact-email-link"
                  className="flex items-center gap-4 group"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gold/25 text-gold group-hover:bg-gold group-hover:text-navy-950 transition-colors duration-300">
                    <Mail size={18} />
                  </span>
                  <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink-muted">Email</div>
                    <div className="text-ink group-hover:text-gold transition-colors">sakimlaw@gmail.com</div>
                  </div>
                </a>
                <a
                  href="tel:+254720577583"
                  data-testid="contact-phone-link"
                  className="flex items-center gap-4 group"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gold/25 text-gold group-hover:bg-gold group-hover:text-navy-950 transition-colors duration-300">
                    <Phone size={18} />
                  </span>
                  <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink-muted">Telephone</div>
                    <div className="text-ink group-hover:text-gold transition-colors">
                      0720 577 583 &nbsp;/&nbsp; 0736 981 614
                    </div>
                  </div>
                </a>
                <div className="flex items-center gap-4" data-testid="contact-address">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gold/25 text-gold">
                    <MapPin size={18} />
                  </span>
                  <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink-muted">Chambers</div>
                    <div className="text-ink">
                      Muthaiga Square, 2nd Floor Room 15, Thika Road, Nairobi
                    </div>
                    <div className="text-sm text-ink-muted mt-0.5">P.O. Box 254720577583 &ndash; 00100</div>
                  </div>
                </div>
              </div>
            </FadeUp>

            <FadeUp delay={0.25}>
              <div
                className="mt-10 rounded-3xl overflow-hidden border border-gold/20"
                data-testid="chambers-map"
              >
                <iframe
                  title="Musesya & Company Advocates — Muthaiga Square, Thika Road, Nairobi"
                  src="https://maps.google.com/maps?q=Muthaiga%20Square%2C%20Thika%20Road%2C%20Nairobi&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  className="w-full h-64 grayscale invert-[0.9] contrast-[0.85] opacity-90"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </FadeUp>
          </div>

          <FadeUp delay={0.2}>
            <form
              onSubmit={submit}
              data-testid="enquiry-form"
              className="rounded-3xl border border-gold/20 bg-navy-900/50 backdrop-blur-xl p-8 sm:p-10 space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <input
                  data-testid="enquiry-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Full name"
                  className={inputCls}
                />
                <input
                  data-testid="enquiry-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="Email address"
                  className={inputCls}
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <input
                  data-testid="enquiry-phone-input"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="Phone (optional)"
                  className={inputCls}
                />
                <select
                  data-testid="enquiry-practice-select"
                  value={form.practice_area}
                  onChange={set("practice_area")}
                  className={`${inputCls} appearance-none ${form.practice_area ? "" : "text-ink-muted"}`}
                >
                  <option value="">Practice area (optional)</option>
                  {PRACTICE_OPTIONS.map((o) => (
                    <option key={o} value={o} className="bg-navy-950 text-ink">
                      {o}
                    </option>
                  ))}
                </select>
              </div>
              <textarea
                data-testid="enquiry-message-input"
                required
                rows={5}
                value={form.message}
                onChange={set("message")}
                placeholder="Briefly describe your matter…"
                className={`${inputCls} resize-none`}
              />
              <button
                data-testid="enquiry-form-submit-button"
                type="submit"
                disabled={sending}
                className="w-full flex items-center justify-center gap-2 bg-gold text-navy-950 font-medium py-4 rounded-full hover:bg-gold-hover transition-colors duration-300 disabled:opacity-60"
              >
                {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
                {sending ? "Sending…" : "Send Enquiry"}
              </button>
              <p className="text-xs text-ink-muted text-center">
                Strictly confidential under The Advocates Practice Rules.
              </p>
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
